import React, { useState, useEffect } from 'react';
import {
  Container, Card, CardContent, Typography, Button, Box,
  CircularProgress, Alert, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Tabs, Tab, IconButton, Chip
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ScienceIcon from '@mui/icons-material/Science';
import * as XLSX from 'xlsx';
import { API_URL } from '../../config';
import WhatsAppSendButton from '../common/WhatsAppSendButton';

const RED = '#7f1d1d';
const RED_BG = '#fef2f2';
const CHEM_COLOR = '#1e3a5f';
const CHEM_BG = '#eff6ff';

const HDR_SX_BASE = {
  fontWeight: 700, color: '#fff', fontSize: '0.68rem', textTransform: 'uppercase',
  borderRight: '1px solid rgba(255,255,255,0.15)', py: 1.0, px: 0.6,
  whiteSpace: 'nowrap', textAlign: 'center',
};

const STORE_HDR_SX = { ...HDR_SX_BASE, backgroundColor: RED };
const CHEM_HDR_SX  = { ...HDR_SX_BASE, backgroundColor: CHEM_COLOR };

const CELL_SX = { py: 0.7, px: 0.6, fontSize: '0.72rem', textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };

function fmtNum(v, d = 0) {
  if (v === null || v === undefined || v === '') return '—';
  const n = parseFloat(v);
  return isNaN(n) ? '—' : n.toFixed(d);
}

function daysColor(v) {
  const n = parseFloat(v);
  if (isNaN(n)) return '#374151';
  if (n < 30) return '#dc2626';
  if (n < 60) return '#d97706';
  return '#166534';
}

// ─── Store Critical Table ─────────────────────────────────────────────────────

const STORE_COLS = [
  { key: 'row_num',             label: 'NO',          w: 40 },
  { key: 'ITEMCODE',            label: 'ITEM CODE',   w: 80 },
  { key: 'ITEMNAME',            label: 'ITEM NAME',   w: 200 },
  { key: 'SPECIFICATION',       label: 'SPEC',        w: 130 },
  { key: 'GROUP',               label: 'GROUP',       w: 90 },
  { key: 'MACHINENAME',         label: 'MACHINE',     w: 130 },
  { key: 'UNIT_TYPE',           label: 'UNIT',        w: 55 },
  { key: 'UNITPRICE',           label: 'PRICE',       w: 80, format: (v) => fmtNum(v, 2) },
  { key: 'STOCK_QTY',           label: 'STOCK',       w: 65 },
  { key: 'UTILIZE_STK',         label: 'UTILIZED',    w: 75 },
  { key: 'DAYS_STOCK',          label: 'DAYS LEFT',   w: 75 },
  { key: 'REQUIRED_STOCK',      label: 'REQ. QTY',    w: 80 },
  { key: 'REQUIRED_STOCK_VALUE',label: 'REQ. VALUE',  w: 95, format: (v) => fmtNum(v, 2) },
  { key: 'UTILIZE_PER_MONTH',   label: 'USE/MONTH',   w: 85, format: (v) => fmtNum(v, 2) },
  { key: 'MONTHS_STOCKLEFT',    label: 'MOS. LEFT',   w: 82, format: (v) => fmtNum(v, 2) },
];

function StoreTable({ rows }) {
  if (!rows || rows.length === 0) return <Alert severity="info" sx={{ m: 2 }}>No store items require restocking.</Alert>;

  const minW = STORE_COLS.reduce((s, c) => s + c.w, 0);

  return (
    <TableContainer sx={{ maxHeight: 580, overflowX: 'auto' }}>
      <Table stickyHeader size="small" sx={{ minWidth: minW }}>
        <TableHead>
          <TableRow>
            {STORE_COLS.map(c => <TableCell key={c.key} sx={{ ...STORE_HDR_SX, minWidth: c.w, width: c.w }}>{c.label}</TableCell>)}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => (
            <TableRow key={i} sx={{
              backgroundColor: i % 2 === 0 ? '#fff' : RED_BG,
              '&:hover': { backgroundColor: '#fee2e2' },
            }}>
              {STORE_COLS.map(c => {
                const raw = row[c.key];
                const val = c.format ? c.format(raw) : (raw ?? '—');
                let cellSx = { ...CELL_SX, minWidth: c.w, width: c.w };
                if (c.key === 'ITEMCODE') cellSx = { ...cellSx, fontWeight: 700, color: RED };
                if (c.key === 'ITEMNAME') cellSx = { ...cellSx, fontWeight: 600, color: CHEM_COLOR, textAlign: 'left' };
                if (c.key === 'STOCK_QTY') cellSx = { ...cellSx, fontWeight: 700, color: '#166534' };
                if (c.key === 'DAYS_STOCK') cellSx = { ...cellSx, fontWeight: 700, color: daysColor(raw) };
                if (c.key === 'REQUIRED_STOCK') cellSx = { ...cellSx, fontWeight: 700, color: '#dc2626', backgroundColor: '#fef2f2' };
                if (c.key === 'REQUIRED_STOCK_VALUE') cellSx = { ...cellSx, fontWeight: 700, color: RED, backgroundColor: '#fef2f2' };
                return <TableCell key={c.key} sx={cellSx}>{val}</TableCell>;
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ─── Chemical Critical Table ──────────────────────────────────────────────────

const CHEM_COLS = [
  { key: 'row_num',       label: 'NO',          w: 40 },
  { key: 'ITEMCODE',      label: 'ITEM CODE',   w: 80 },
  { key: 'ITEMNAME',      label: 'ITEM NAME',   w: 220 },
  { key: 'SPECIFICATION', label: 'SPEC',        w: 130 },
  { key: 'GROUP',         label: 'GROUP',       w: 90 },
  { key: 'MACHINENAME',   label: 'MACHINE',     w: 130 },
  { key: 'UNIT_TYPE',     label: 'UNIT',        w: 55 },
  { key: 'UNITPRICE',     label: 'PRICE',       w: 90, format: (v) => fmtNum(v, 2) },
  { key: 'CURRENCY',      label: 'CCY',         w: 55 },
  { key: 'STOCK_QTY',     label: 'STOCK',       w: 65 },
  { key: 'UTILIZE_STK',   label: 'UTILIZED',    w: 75 },
  { key: 'DAYS_STOCK',    label: 'DAYS LEFT',   w: 80 },
  { key: 'AVG_UTILIZE',   label: 'AVG/DAY',     w: 80 },
];

function ChemTable({ rows }) {
  if (!rows || rows.length === 0) return <Alert severity="info" sx={{ m: 2 }}>No chemical critical data.</Alert>;

  const minW = CHEM_COLS.reduce((s, c) => s + c.w, 0);

  return (
    <TableContainer sx={{ maxHeight: 580, overflowX: 'auto' }}>
      <Table stickyHeader size="small" sx={{ minWidth: minW }}>
        <TableHead>
          <TableRow>
            {CHEM_COLS.map(c => <TableCell key={c.key} sx={{ ...CHEM_HDR_SX, minWidth: c.w, width: c.w }}>{c.label}</TableCell>)}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => (
            <TableRow key={i} sx={{
              backgroundColor: i % 2 === 0 ? '#fff' : CHEM_BG,
              '&:hover': { backgroundColor: '#dbeafe' },
            }}>
              {CHEM_COLS.map(c => {
                const raw = row[c.key];
                const val = c.format ? c.format(raw) : (raw ?? '—');
                let cellSx = { ...CELL_SX, minWidth: c.w, width: c.w };
                if (c.key === 'ITEMCODE') cellSx = { ...cellSx, fontWeight: 700, color: CHEM_COLOR };
                if (c.key === 'ITEMNAME') cellSx = { ...cellSx, fontWeight: 600, color: CHEM_COLOR, textAlign: 'left' };
                if (c.key === 'STOCK_QTY') cellSx = { ...cellSx, fontWeight: 700, color: '#166534' };
                if (c.key === 'DAYS_STOCK') cellSx = { ...cellSx, fontWeight: 700, color: daysColor(raw) };
                if (c.key === 'AVG_UTILIZE') cellSx = { ...cellSx, fontWeight: 600, color: RED };
                return <TableCell key={c.key} sx={cellSx}>{val}</TableCell>;
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ─── Main view ────────────────────────────────────────────────────────────────

const TAB_LABELS = ['STORE CRITICAL', 'CHEMICAL CRITICAL'];
const TAB_ICONS = [<WarningAmberIcon fontSize="small" />, <ScienceIcon fontSize="small" />];

const CriticalStockReportView = ({ onBackClick, user }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState('');
  const [data, setData]     = useState(null);
  const [tab, setTab]       = useState(0);

  const fetchData = async () => {
    setLoading(true); setError(''); setData(null);
    try {
      const res = await fetch(`${API_URL}/order/criticalstockreport`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!res.ok) throw new Error('API error: ' + res.statusText);
      const apiData = await res.json();
      if (!apiData.success || !apiData.data) {
        setError(apiData.message || 'Failed to fetch critical stock data.');
        return;
      }
      setData(apiData.data);
    } catch (e) {
      setError(e.message || 'Fetch failed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleDownload = () => {
    if (!data) return;
    const wb = XLSX.utils.book_new();
    const storeRows = data[0] || [];
    const chemRows  = data[1] || [];
    if (storeRows.length) XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(storeRows), 'Store Critical');
    if (chemRows.length)  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(chemRows),  'Chemical Critical');
    const today = new Date().toISOString().substring(0, 10);
    XLSX.writeFile(wb, `CriticalStock_${today}.xlsx`);
  };

  const storeCount = (data?.[0] || []).length;
  const chemCount  = (data?.[1] || []).length;

  return (
    <Container maxWidth="xl" sx={{ mt: 2, pb: 4, px: { xs: 1, md: 2 } }}>

      {/* Header card */}
      <Card sx={{ mb: 3, borderRadius: 3, boxShadow: '0 4px 12px rgba(127,29,29,0.1)', border: '1px solid #fecaca' }}>
        <CardContent sx={{ p: 3 }}>
          <Box display="flex" alignItems="center" mb={2}>
            {onBackClick && (
              <IconButton onClick={onBackClick} sx={{ mr: 2, color: RED }}>
                <ArrowBackIcon />
              </IconButton>
            )}
            <Box sx={{ background: `linear-gradient(135deg, ${RED} 0%, #b91c1c 100%)`, borderRadius: 2, p: 1.5, mr: 2 }}>
              <WarningAmberIcon sx={{ fontSize: 28, color: '#fff' }} />
            </Box>
            <Box flex={1}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: RED }}>Critical Stock Report</Typography>
              <Typography variant="body2" sx={{ color: '#666' }}>
                Store &amp; Chemical critical items based on recent utilization
              </Typography>
            </Box>
            <Button
              variant="outlined"
              size="medium"
              startIcon={<RefreshIcon />}
              onClick={fetchData}
              disabled={loading}
              sx={{
                color: RED, borderColor: '#fecaca', textTransform: 'none', fontWeight: 600,
                '&:hover': { borderColor: RED, backgroundColor: RED_BG },
              }}
            >
              Refresh
            </Button>
          </Box>

          <Box display="flex" flexWrap="wrap" gap={1.5}>
            <Chip
              icon={<WarningAmberIcon />}
              label={`Store: ${storeCount} items need restock`}
              color="error"
              variant="outlined"
              sx={{ fontWeight: 600 }}
            />
            <Chip
              icon={<ScienceIcon />}
              label={`Chemical: ${chemCount} items tracked`}
              color="primary"
              variant="outlined"
              sx={{ fontWeight: 600, color: CHEM_COLOR, borderColor: CHEM_COLOR }}
            />
            <Chip
              label="Based on recent utilization"
              variant="filled"
              sx={{ backgroundColor: '#fef3c7', color: '#92400e', fontWeight: 600 }}
            />
          </Box>
        </CardContent>
      </Card>

      {loading && <Box textAlign="center" py={6}><CircularProgress sx={{ color: RED }} /></Box>}
      {error   && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {data && (
        <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', border: '1px solid #fecaca' }}>
          <Box sx={{
            borderBottom: 1, borderColor: 'divider',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            px: 2, pt: 1,
          }}>
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              textColor="inherit"
              sx={{
                '& .MuiTab-root': { fontWeight: 600, fontSize: '0.78rem', minHeight: 48, flexDirection: 'row', gap: 0.5 },
                '& .Mui-selected': { color: tab === 0 ? RED : CHEM_COLOR },
                '& .MuiTabs-indicator': { backgroundColor: tab === 0 ? RED : CHEM_COLOR },
              }}
            >
              {TAB_LABELS.map((l, i) => (
                <Tab key={i} icon={TAB_ICONS[i]} iconPosition="start" label={`${l} (${i === 0 ? storeCount : chemCount})`} />
              ))}
            </Tabs>
            <Button size="small" startIcon={<DownloadIcon />} onClick={handleDownload}
              sx={{ color: RED, fontWeight: 600, textTransform: 'none', ml: 1, flexShrink: 0 }}>
              Excel
            </Button>
          </Box>

          {tab === 0 && <StoreTable rows={data[0]} />}
          {tab === 1 && <ChemTable  rows={data[1]} />}
        </Card>
      )}

      {data && (
        <Box sx={{ mt: 3, display: 'flex', justifyContent: { xs: 'center', md: 'flex-end' } }}>
          <WhatsAppSendButton
            user={user}
            label="Send Critical Stock Report to WhatsApp"
            apiUrl={`${API_URL}/api/critical-stock/send`}
            payload={{}}
          />
        </Box>
      )}
    </Container>
  );
};

export default CriticalStockReportView;
